/**
 * HTTP Client - apiRequest + api helper
 * Handles all HTTP requests with standardized error handling
 */

import { setMaintenance } from '../../composables/useMaintenance.js'
import { notifySessionExpired } from './session-expiry.js'

/**
 * Effectue une requete HTTP avec gestion d'erreur standardisee
 * @param {string} url - URL de l'endpoint
 * @param {Object} options - Options de la requete (method, body, headers, etc.)
 * @returns {Promise<Object>} - Donnees de la reponse
 * @throws {Error} - Erreur avec message detaille
 */
export async function apiRequest(url, options = {}) {
  const {
    method = 'GET',
    body = null,
    headers = {},
    responseType = null,
    ...restOptions
  } = options

  // Detecter si le body est un FormData
  const isFormData = body instanceof FormData

  // Headers par defaut
  // Ne pas definir Content-Type pour FormData (le navigateur le fait automatiquement avec le boundary)
  const defaultHeaders = isFormData
    ? { ...headers }
    : {
        'Content-Type': 'application/json',
        ...headers
      }

  // Configuration de la requete
  const config = {
    method,
    headers: defaultHeaders,
    credentials: 'include', // Toujours inclure les cookies pour l'authentification
    ...restOptions
  }

  // Ajouter le body si present
  if (body && method !== 'GET') {
    if (isFormData) {
      // Pour FormData, passer le body tel quel (ne pas convertir en JSON)
      config.body = body
    } else {
      // Pour les autres types, convertir en JSON si necessaire
      config.body = typeof body === 'string' ? body : JSON.stringify(body)
    }
  }

  try {
    const response = await fetch(url, config)

    // Telechargement de fichier force (CSV/Excel/PDF...) : on veut toujours un Blob,
    // quel que soit le Content-Type. Sans ca, un text/csv est lu comme du texte (string)
    // et le telechargement echoue cote appelant.
    if (responseType === 'blob') {
      if (!response.ok) {
        let errMessage = `HTTP error! status: ${response.status}`
        try {
          const errData = await response.json()
          errMessage = errData?.error || errData?.message || errData?.details || errMessage
        } catch {
          // corps non JSON : on garde le message par defaut
        }
        const httpError = new Error(errMessage)
        httpError.status = response.status
        if (response.status === 401) {
          notifySessionExpired(url)
        }
        throw httpError
      }
      return await response.blob()
    }

    // Gerer les reponses non-JSON (comme les fichiers)
    const contentType = response.headers.get('content-type')
    const isJson = contentType && contentType.includes('application/json')

    let data
    if (isJson) {
      data = await response.json()
    } else {
      // Pour les fichiers, retourner le blob ou le texte
      if (contentType?.includes('application/')) {
        data = await response.blob()
      } else {
        data = await response.text()
      }
    }

    // Detecter le mode maintenance (503 renvoye par le middleware backend)
    if (response.status === 503 && data?.maintenance) {
      setMaintenance(true, data.message)
      const maintenanceError = new Error(data.message || 'Application en cours de mise a jour')
      maintenanceError.status = 503
      throw maintenanceError
    }

    // Gerer les erreurs HTTP
    if (!response.ok) {
      // Erreur de validation Zod : les messages utiles sont dans details.fields,
      // pas dans data.error (generique "Erreur de validation"). Un tableau de
      // champs manquants (Errors.missingFields) ne porte que des noms : le
      // message du serveur les liste déjà.
      const fields = data?.details?.fields
      const fieldMessages = fields && typeof fields === 'object' && !Array.isArray(fields)
        ? Object.values(fields).filter((m) => typeof m === 'string')
        : []
      const errorMessage = (fieldMessages.length > 0 && fieldMessages.join('. ')) ||
                           data?.error ||
                           data?.message ||
                           (typeof data?.details === 'string' ? data.details : null) ||
                           `HTTP error! status: ${response.status}`
      const httpError = new Error(errorMessage)
      httpError.status = response.status
      // Session expirée : prévenu ici pour TOUTE l'application, sinon chaque
      // écran affichait le message d'authentification du serveur au bout de son
      // propre formulaire, sans jamais renvoyer vers la reconnexion.
      if (response.status === 401) {
        notifySessionExpired(url)
      }
      // Le contexte machine renvoyé par l'API (`details`) suit l'erreur : sans
      // lui, un appelant ne peut distinguer deux refus de même statut — par
      // exemple un e-mail déjà pris d'une passerelle vers l'autre réseau.
      if (data?.details && typeof data.details === 'object') {
        httpError.details = data.details
      }
      if (fieldMessages.length > 0) {
        httpError.fields = data.details.fields
      }
      throw httpError
    }

    return data
  } catch (error) {
    // Si c'est deja une Error avec un message, la relancer
    if (error instanceof Error) {
      throw error
    }
    // Sinon, creer une nouvelle Error
    throw new Error(error.message || 'Une erreur est survenue lors de la requete')
  }
}

/**
 * Methodes HTTP simplifiees
 */
export const api = {
  get: (url, options = {}) => apiRequest(url, { ...options, method: 'GET' }),

  post: (url, body, options = {}) =>
    apiRequest(url, { ...options, method: 'POST', body }),

  put: (url, body, options = {}) =>
    apiRequest(url, { ...options, method: 'PUT', body }),

  patch: (url, body, options = {}) =>
    apiRequest(url, { ...options, method: 'PATCH', body }),

  delete: (url, options = {}) =>
    apiRequest(url, { ...options, method: 'DELETE' })
}
